/**
 * Centralized Status Helper Utility
 * Standardizes status chip colors across the application
 */

// Centralized color palette constants.
// Modify these hex codes in one place to update colors across the whole application.
export const COLORS = {
  grey: '#8c8c8c',
  darkGrey: '#595959',
  lightGrey: '#bfbfbf',
  orange: '#fa8c16',
  gold: '#faad14',
  brightCyan: '#36cfc9',
  darkTeal: '#08979c',
  royalBlue: '#096dd9',
  blue: '#1890ff',
  darkIndigo: '#1d39c4',
  limeGreen: '#a0d911',
  mutedBlue: '#40a9ff',
  deepBlue: '#0050b3',
  indigo: '#2f54eb',
  mutedPurple: '#9254de',
  purple: '#722ed1',
  tealCyan: '#13c2c2',
  yellowOrange: '#ffa940',
  green: '#52c41a',
  forestGreen: '#389e0d',
  red: '#f5222d',
};

// Centralized status color configuration registry.
// Maps each status to a specific constant from the COLORS palette.
export const STATUS_COLORS = {
  // Draft / Inactive / Default states
  draft: COLORS.grey,
  inactive: COLORS.darkGrey,
  deactive: COLORS.darkGrey,
  deactivated: COLORS.darkGrey,
  no: COLORS.lightGrey,
  default: COLORS.lightGrey,

  // Initial states / Pending
  pending: COLORS.orange,
  requested: COLORS.orange,
  open: COLORS.orange,
  reopen: COLORS.orange,
  created: COLORS.gold,

  // Approval stages
  pending_approval: COLORS.brightCyan,
  pending_1st: COLORS.brightCyan,
  pending_1st_approval: COLORS.brightCyan,
  pending_2nd: COLORS.darkTeal,
  pending_2nd_approval: COLORS.darkTeal,
  first_approved: COLORS.royalBlue,
  first_approval: COLORS.royalBlue,
  approved: COLORS.blue,
  fully_approved: COLORS.blue,
  success: COLORS.blue,
  accepted: COLORS.blue,
  good: COLORS.blue,

  // Progress / Processing states
  processing: COLORS.darkIndigo,
  in_product: COLORS.darkIndigo,
  inprogress: COLORS.darkIndigo,
  in_progress: COLORS.darkIndigo,
  preparing: COLORS.darkIndigo,
  scheduled: COLORS.darkIndigo,
  active: COLORS.limeGreen,
  ready: COLORS.mutedBlue,
  normal: COLORS.deepBlue,
  yes: COLORS.deepBlue,
  blue: COLORS.deepBlue,

  // Delivery / Dispatch states
  delivered: COLORS.indigo,
  dispatched: COLORS.mutedPurple,
  shipped: COLORS.mutedPurple,

  // Payment / Paid states
  paid: COLORS.purple,
  fully_paid: COLORS.purple,
  full_paid: COLORS.purple,
  partial: COLORS.tealCyan,
  partial_issued: COLORS.tealCyan,
  partially_accepted: COLORS.tealCyan,
  partial_delivered: COLORS.tealCyan,
  partially_paid: COLORS.tealCyan,
  partial_paid: COLORS.tealCyan,
  partially: COLORS.tealCyan,
  partially_received: COLORS.tealCyan,
  average: COLORS.tealCyan,
  hold: COLORS.yellowOrange,

  // Complete / Completed states
  complete: COLORS.green,
  completed: COLORS.forestGreen,
  issued: COLORS.forestGreen,
  received: COLORS.forestGreen,

  // Error / Failed / Rejected states
  rejected: COLORS.red,
  failed: COLORS.red,
  error: COLORS.red,
  unpaid: COLORS.red,
  cancelled: COLORS.red,
  returned: COLORS.red,
  closed: COLORS.red,
  urgent: COLORS.red,
  critical: COLORS.red,
  blocked: COLORS.red,
  poor: COLORS.red,
};

const HEX_TO_PRESET = {
  '#8c8c8c': 'default',
  '#595959': 'default',
  '#bfbfbf': 'default',
  '#fa8c16': 'orange',
  '#ffa940': 'orange',
  '#faad14': 'gold',
  '#36cfc9': 'cyan',
  '#08979c': 'cyan',
  '#13c2c2': 'cyan',
  '#096dd9': 'geekblue',
  '#2f54eb': 'geekblue',
  '#1890ff': 'blue',
  '#40a9ff': 'blue',
  '#0050b3': 'blue',
  '#1d39c4': 'purple',
  '#9254de': 'purple',
  '#722ed1': 'purple',
  '#a0d911': 'lime',
  '#52c41a': 'green',
  '#389e0d': 'green',
  '#f5222d': 'red',
};

/**
 * Returns a standardized preset color name for a given status.
 * Used for Ant Design Tag components to style them as transparent chips.
 */
export const getStatusColor = (status) => {
  if (!status) return 'default';
  const normalizedKey = status
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_'); // normalize spaces & hyphens to underscores
    
  const hexColor = STATUS_COLORS[normalizedKey] || STATUS_COLORS.default;
  return HEX_TO_PRESET[hexColor.toLowerCase()] || 'default';
};

/**
 * Returns a standardized hex color for a given status.
 * Used for custom SVG drawings and charts (like DoughnutChart).
 */
export const getStatusHexColor = (status) => {
  if (!status) return STATUS_COLORS.default;
  const normalizedKey = status
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_'); // normalize spaces & hyphens to underscores
    
  return STATUS_COLORS[normalizedKey] || STATUS_COLORS.default;
};
